/* =====================================================================
 * AdminOverviewPanel — 앱 종합 분석 개요 (owner 전용, 개요 탭)
 *
 * Postgres 집계(user_usage_ledger + error_events) 기반. 기간 프리셋(오늘/7일/
 * 30일)·커스텀 범위, KPI(활성/ DAU/WAU/MAU/평균이벤트/토큰/에러) + ? 호버 설명,
 * 일자별 시계열 라인차트 + 기능별 막대차트. 전 환경(local/dev/prod) 균일.
 * ===================================================================== */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Loader2, Users, Zap, Activity, AlertTriangle, UserCheck, TrendingUp, HelpCircle, RefreshCw } from 'lucide-react';
import { useChatStore } from '@auw/stores';
import { getOverviewAnalytics, type OverviewAnalyticsResponse } from '@auw/api-client';
import { LineChart, FeatureBars } from './AdminCharts';

type Preset = 'today' | 'week' | 'month' | 'custom';

const kstTodayISO = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());

const addDaysISO = (iso: string, days: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
};

const fmtFull = (n: number | undefined): string => (n ?? 0).toLocaleString();
// KPI 카드: 큰 수는 compact(예: 49.9M)로 표기해 카드 폭을 넘지 않게 한다(full 값은 title).
const fmtKpi = (n: number | undefined): string => {
  const v = n ?? 0;
  return Math.abs(v) >= 100000
    ? new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(v)
    : v.toLocaleString();
};

export const AdminOverviewPanel: React.FC = () => {
  const { t } = useTranslation('admin');
  const addToast = useChatStore((s) => s.addToast);

  const today = kstTodayISO();
  const [preset, setPreset] = useState<Preset>('week');
  const [from, setFrom] = useState(() => addDaysISO(today, -6));
  const [to, setTo] = useState(today);
  const [data, setData] = useState<OverviewAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (dateFrom: string, dateTo: string) => {
    setLoading(true);
    try {
      setData(await getOverviewAnalytics({ dateFrom, dateTo }));
    } catch (e) {
      addToast({ type: 'error', title: t('appManage.overview.loadFail'), description: e instanceof Error ? e.message : undefined });
    } finally {
      setLoading(false);
    }
  }, [addToast, t]);

  useEffect(() => { void load(from, to); }, [load, from, to]);

  const applyPreset = (p: Preset) => {
    const td = kstTodayISO();
    setPreset(p);
    if (p === 'today') { setFrom(td); setTo(td); }
    else if (p === 'week') { setFrom(addDaysISO(td, -6)); setTo(td); }
    else if (p === 'month') { setFrom(addDaysISO(td, -29)); setTo(td); }
  };

  const series = data?.series ?? [];
  const activeSeries = series.map((s) => ({ label: s.date.slice(5), value: s.active_users }));
  const eventSeries = series.map((s) => ({ label: s.date.slice(5), value: s.events }));
  const featureData = (data?.by_feature ?? []).map((f) => ({
    label: t(`appManage.features.${f.feature}`, f.feature),
    value: f.count,
  }));

  return (
    <>
      <Controls>
        <Presets>
          {(['today', 'week', 'month'] as Preset[]).map((p) => (
            <PresetBtn key={p} type="button" $active={preset === p} onClick={() => applyPreset(p)}>
              {t(`appManage.overview.preset_${p}`)}
            </PresetBtn>
          ))}
        </Presets>
        <Range>
          <DateInput type="date" value={from} max={to} onChange={(e) => { setPreset('custom'); setFrom(e.target.value); }} />
          <span>~</span>
          <DateInput type="date" value={to} max={today} min={from} onChange={(e) => { setPreset('custom'); setTo(e.target.value); }} />
          <RefreshBtn type="button" onClick={() => void load(from, to)} disabled={loading} aria-label={t('appManage.refresh')}>
            <RefreshCw size={15} className={loading ? 'spin' : undefined} />
            {t('appManage.refresh')}
          </RefreshBtn>
        </Range>
      </Controls>

      {loading && !data ? (
        <Center><Loader2 size={20} className="spin" /></Center>
      ) : !data ? (
        <Center>{t('appManage.overview.loadFail')}</Center>
      ) : (
        <>
          <Kpis>
            <Kpi $c="#3b82f6" icon={<Users size={18} />} label={t('appManage.overview.activeUsers')} tip={t('appManage.overview.tips.activeUsers')} value={fmtKpi(data.active_users)} full={fmtFull(data.active_users)} />
            <Kpi $c="#0ea5e9" icon={<UserCheck size={18} />} label={t('appManage.overview.dau')} tip={t('appManage.overview.tips.dau')} value={fmtKpi(data.dau)} full={fmtFull(data.dau)} />
            <Kpi $c="#6366f1" icon={<UserCheck size={18} />} label={t('appManage.overview.wau')} tip={t('appManage.overview.tips.wau')} value={fmtKpi(data.wau)} full={fmtFull(data.wau)} />
            <Kpi $c="#8b5cf6" icon={<UserCheck size={18} />} label={t('appManage.overview.mau')} tip={t('appManage.overview.tips.mau')} value={fmtKpi(data.mau)} full={fmtFull(data.mau)} />
            <Kpi $c="#10b981" icon={<Zap size={18} />} label={t('appManage.overview.totalTokens')} tip={t('appManage.overview.tips.totalTokens')} value={fmtKpi(data.total_tokens)} full={fmtFull(data.total_tokens)} />
            <Kpi $c="#a855f7" icon={<Activity size={18} />} label={t('appManage.overview.totalEvents')} tip={t('appManage.overview.tips.totalEvents')} value={fmtKpi(data.total_events)} full={fmtFull(data.total_events)} />
            <Kpi $c="#f59e0b" icon={<TrendingUp size={18} />} label={t('appManage.overview.avgEvents')} tip={t('appManage.overview.tips.avgEvents')} value={fmtKpi(data.avg_events_per_user)} full={String(data.avg_events_per_user ?? 0)} />
            <Kpi $c="#ef4444" icon={<AlertTriangle size={18} />} label={t('appManage.overview.errorCount')} tip={t('appManage.overview.tips.errorCount')} value={fmtKpi(data.error_count)} full={fmtFull(data.error_count)} />
          </Kpis>

          <RangeNote>{t('appManage.overview.range', { from: data.from_date, to: data.to_date })}</RangeNote>

          <Charts>
            <ChartCard>
              <ChartTitle>{t('appManage.overview.dailyActiveUsers')}<InfoTip text={t('appManage.overview.tips.dailyActiveUsers')} /></ChartTitle>
              {activeSeries.length > 0 ? <LineChart data={activeSeries} color="#3b82f6" /> : <Center>{t('appManage.overview.noData')}</Center>}
            </ChartCard>
            <ChartCard>
              <ChartTitle>{t('appManage.overview.dailyEvents')}<InfoTip text={t('appManage.overview.tips.dailyEvents')} /></ChartTitle>
              {eventSeries.length > 0 ? <LineChart data={eventSeries} color="#a855f7" /> : <Center>{t('appManage.overview.noData')}</Center>}
            </ChartCard>
          </Charts>

          <SectionTitle>{t('appManage.overview.byFeature')}</SectionTitle>
          {featureData.length > 0 ? <FeatureBars data={featureData} /> : <Center>{t('appManage.overview.noData')}</Center>}
        </>
      )}
    </>
  );
};

export default AdminOverviewPanel;

/* ---- KPI + InfoTip ---- */

const Kpi: React.FC<{ $c: string; icon: React.ReactNode; label: string; tip: string; value: string; full?: string }> = ({ $c, icon, label, tip, value, full }) => (
  <KpiCard>
    <KpiIcon $c={$c}>{icon}</KpiIcon>
    <KpiBody>
      <KpiLabel>{label}<InfoTip text={tip} /></KpiLabel>
      <KpiValue title={full ?? value}>{value}</KpiValue>
    </KpiBody>
  </KpiCard>
);

const InfoTip: React.FC<{ text: string }> = ({ text }) => (
  <Tip tabIndex={0}>
    <HelpCircle size={12} />
    <TipBox role="tooltip">{text}</TipBox>
  </Tip>
);

/* ------------------------------------------------------------------ */

const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;

  /* 모바일: 프리셋과 기간 선택을 세로로 쌓아 각 행을 가득 채운다. */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Presets = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  background: ${({ theme }) => theme.colors.bgSecondary};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: flex;
  }
`;

const PresetBtn = styled.button<{ $active?: boolean }>`
  appearance: none;
  border: none;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  font-size: 0.8125rem;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  color: ${({ $active, theme }) => ($active ? theme.colors.textPrimary : theme.colors.textMuted)};
  background: ${({ $active, theme }) => ($active ? theme.colors.bgPrimary : 'transparent')};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex: 1;
  }
`;

const Range = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;

  /* 모바일: 날짜 입력 2개 + 새로고침이 한 줄을 넘치지 않게 줄바꿈 + 폭 채움 */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: flex;
    flex-wrap: wrap;
  }
`;

const RefreshBtn = styled.button`
  appearance: none; display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 6px 12px; border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary}; font-size: 0.8125rem; cursor: pointer;
  &:hover { border-color: ${({ theme }) => theme.colors.borderInput}; }
  &:disabled { opacity: 0.6; cursor: default; }
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex: 1 0 100%;
  }
`;

const DateInput = styled.input`
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.8125rem;
  /* 다크모드에서 네이티브 달력 아이콘이 흰색으로 보이도록 색상 스킴을 테마에 맞춘다. */
  color-scheme: ${({ theme }) => (theme.mode === 'dark' ? 'dark' : 'light')};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex: 1;
    min-width: 0;
  }
`;

const Kpis = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const KpiCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
`;

const KpiIcon = styled.div<{ $c: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: #fff;
  background: ${({ $c }) => $c};
  flex-shrink: 0;
`;

const KpiBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const KpiLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const KpiValue = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-variant-numeric: tabular-nums;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RangeNote = styled.p`
  margin: 14px 0 4px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Charts = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-top: 8px;
`;

const ChartCard = styled.div`
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
`;

const ChartTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 12px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const SectionTitle = styled.h3`
  margin: 28px 0 12px;
  font-size: 0.9375rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Tip = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: help;
  outline: none;
`;

const TipBox = styled.span`
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  max-width: 240px;
  padding: 8px 10px;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) => theme.colors.textPrimary};
  color: ${({ theme }) => theme.colors.bgPrimary};
  font-size: 0.6875rem;
  font-weight: 400;
  line-height: 1.4;
  white-space: normal;
  text-align: left;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s ease;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  ${Tip}:hover &, ${Tip}:focus &, ${Tip}:focus-within & { opacity: 0.96; }
`;
